/**
 * AUDIT LOG SETTINGS (TS-33) — Applicazione "Log di audit" del pannello
 * impostazioni: mostra all'utente la cronologia dei propri accessi
 * (login / logout), ricavata dal framework di audit di Liferay.
 *
 * Dati reali da GET /user/auditLog (Page<AccessEntry>), paginata server-side
 * via lo slice Redux `auditLog`. DataGrid in paginationMode="server" come le
 * altre liste del CRM. L'ordinamento è fisso lato server (data desc), quindi
 * le colonne non sono ordinabili.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { formatDateTime, useServerListParams } from '@crm/shared';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchAuditLog } from '../../entities/auditLog/auditLogThunks';

export function AuditLogSettings() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { items, status, error, totalCount } = useAppSelector((state) => state.auditLog);
  const { paginationModel, setPaginationModel, backendParams } = useServerListParams();

  useEffect(() => {
    dispatch(fetchAuditLog({ page: backendParams.page, pageSize: backendParams.pageSize }));
  }, [backendParams, dispatch]);

  if (status === 'failed') return <p className="text-error">{t('common.error', { message: error })}</p>;

  return (
    <div className="audit-log">
      <p className="audit-log__intro">{t('userSettings.audit.intro')}</p>
      {status === 'succeeded' && items.length === 0 ? (
        <p className="text-muted">{t('userSettings.audit.empty')}</p>
      ) : (
        <DataGrid
          rows={items}
          columns={auditColumns(t)}
          getRowId={(row) => row.id}
          autoHeight
          disableRowSelectionOnClick
          disableColumnMenu
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          loading={status === 'loading'}
          getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? '' : 'even-row')}
          className="datagrid-standard"
          sx={{ border: 'none' }}
        />
      )}
    </div>
  );
}

function auditColumns(
  t: (key: string, opts?: Record<string, unknown>) => string,
): GridColDef[] {
  return [
    {
      field: 'date', headerName: t('userSettings.audit.columns.date'), width: 180, sortable: false,
      renderCell: (params) => formatDateTime(params.row.date),
    },
    {
      field: 'action', headerName: t('userSettings.audit.columns.action'), width: 140, sortable: false,
      renderCell: (params) => t(`userSettings.audit.actions.${params.row.action}`),
    },
    { field: 'ip', headerName: t('userSettings.audit.columns.ip'), width: 160, sortable: false },
    { field: 'browser', headerName: t('userSettings.audit.columns.browser'), flex: 1, sortable: false },
  ];
}
